import {createAsyncThunk, createSlice, Dispatch, PayloadAction} from "@reduxjs/toolkit";
import {AppDispatch, RootState} from "../../configureStore";

import _ from "lodash";
import {findQuery, findStudy, getCurrent, isDefined, monthsRange, randomId, randomInt} from "../../reducers/util";
import {AppState, CurrentState} from "../../types/AppState";
import {Project} from "../../types/Project";
import {CohortDataset, Query} from "../HealthcareUsageReport/types/CohortQuery";
import {
    apiCreateStudy,
    apiDeleteQuery,
    apiDeleteStudy,
    apiFetchCohortBuilderColleagues,
    apiFetchExampleProjects,
    apiFetchProjects,
    apiFetchSharedProjects,
    apiFetchSharedProjectsUnreadCount,
    apiRefreshQueryRequests,
    apiRunQuery,
    apiSaveQuery,
    apiStopQuery,
    apiUpdateQuery,
    apiUpdateStudy
} from "./cohortBuilderApi";
import {queryDescription} from "./CohortUtil";
import {_addCriterion, _deleteCriterion, _operatorCriterion, CriterionPayload} from "./criterion";
import {_addGroup, _copyGroup, _deleteGroup, _setGroupTitle, setOperatorOfGroup} from "./groups";
import {UserDetails} from "../../actions/api";


// TODO: do lazy fetch of just the studies and not the queries within but the length of queries
export const fetchProjects = createAsyncThunk(
    'cohortBuilder/fetchProjects',
    async () => {
        return await apiFetchProjects()
    }
)

export const fetchExampleProjects = createAsyncThunk(
    'cohortBuilder/fetchExampleProjects',
    async () => {
        return await apiFetchExampleProjects()
    }
)

export const fetchSharedProjects = createAsyncThunk(
    'cohortBuilder/fetchSharedProjects',
    async () => {
        return await apiFetchSharedProjects()
    }
)

export const fetchSharedProjectsUnreadCount = createAsyncThunk(
    'cohortBuilder/fetchSharedProjectsUnreadCount',
    async () => {
        return await apiFetchSharedProjectsUnreadCount()
    }
)

export const markQueryRead = createAsyncThunk<Query | undefined, number | undefined, {state: RootState}>(
    'cohortBuilder/markQueryUnread',
    async(queryId: number | undefined, thunkApi) => {
            if (queryId === undefined) {
                return undefined
            }
            const query = findQuery(thunkApi.getState().current.study, queryId)
            if (query?.unread) {
                return await apiUpdateQuery({...query, unread: false})
            }
        }
)

export const fetchColleagues = createAsyncThunk<UserDetails[]>(
    'cohortBuilder/fetchColleagues',
    async() => {
        return apiFetchCohortBuilderColleagues()
    }
)

export const saveThenRunThenUpdateQuery = (queryId: number) => {
    return (dispatch: Dispatch<any>, getState: () => RootState) => {
        return doSave(dispatch, getState().current, queryId)
            .then(() => {
                const query = findQuery(getState().current.study, queryId)
                const description = queryDescription(query!)
                apiRunQuery(queryId, description)
                    .then(response => response.json())
                    .then(json => {
                        const sparkQueries = { [queryId]: { sparkQuery: json } }
                        dispatch(mergeQueryRequests(sparkQueries))
                    })
            })
    }
}

export const doSave = async (dispatch: Dispatch<any>, state: CurrentState, queryId: number) => {
    const query = findQuery(state.study, queryId)
    const activeKey = state.activeKeys[queryId]
    const savedQuery = await apiSaveQuery(state.study.id, query!)
    dispatch(receiveQuery({ queryId, query: savedQuery }))
    // use new queryId if it has changed
    dispatch(setCurrentQuery(savedQuery.id))
    dispatch(setActiveKey({ queryId: savedQuery.id, activeKey }))
    return savedQuery
}

export const saveAndUpdateQuery = (queryId: number) => {
    return async (dispatch: Dispatch<any>, getState: () => RootState) => {
        const state = getState().current
        return doSave(dispatch, state, queryId)
    }
}

export const refreshQueryRequests = (queryIDs: number[]) => {
    return (dispatch: Dispatch<any>) => {
        return apiRefreshQueryRequests(queryIDs)
            .then(json => {
                dispatch(mergeQueryRequests(json))
            })
    }
}

export const deleteQuery = (queryId: number) => {
    return (dispatch: AppDispatch, getState: () => AppState) => {
        return apiDeleteQuery(queryId)
            .then(() => {
                 dispatch(deleteQueryFromState(queryId))
                return getState().current.study
            })
    }
}

export const stopQuery = (queryId: number, sparkQueryId: number) => {
    return (dispatch: Dispatch<any>) => {
        return apiStopQuery(sparkQueryId)
            .then(() => {
                dispatch(fetchProjects())
            })
            .then(() => {
                dispatch(refreshQueryRequests([queryId]))
            })
    }
}

export const createStudy = (title: string, description: string) => {
    return apiCreateStudy({ title, description })
}

export const deleteStudy = (studyId: number, isShared: boolean) => {
    return (dispatch: Dispatch<any>) => {
        return apiDeleteStudy(studyId)
            .then(() => {
                return dispatch(isShared ? fetchSharedProjects() : fetchProjects())
            })
    }
}

const initialState: CurrentState = {
    studies: undefined,
    // @ts-ignore
    study: null,
    queryId: null,
    groupId: null,
    criterionId: null,
    isFetchingQuery: false,
    activeKeys: {},
    example: false,
    colleagues: undefined
}

export const cohortBuilderSlice = createSlice({
    name: 'cohortBuilder',
    initialState,
    reducers: {
        saveStudy: (state, action: PayloadAction<Project>) => {
            apiUpdateStudy(action.payload)
        },
        setCurrentQuery: (state, action: PayloadAction<number>) => {
            state.queryId = action.payload
            state.groupId = null
            state.criterionId = null
            initActiveKeys(state, state.queryId)
        },
        setCurrentStudy: (state, action: PayloadAction<Project>) => {
            state.study = action.payload
            state.queryId = state.study!.queries.length > 0 ? state.study!.queries[0].id : null
            state.groupId = null
            state.criterionId = null
            initActiveKeys(state, state.queryId)
        },
        setCurrentCriterion: (state, action: PayloadAction<{ queryId: number, groupId: string, criterionId: string }>) => {
            state.queryId = action.payload.queryId
            state.groupId = action.payload.groupId
            state.criterionId = action.payload.criterionId
        },
        setProjectTitle: (state, action: PayloadAction<string>) => {
            const title = action.payload;
            state.study.title = title;

            // clone to prevent reloading as not everything is saved to server
            const studyClone = JSON.parse(JSON.stringify(findStudy(state.studies, state.study.id))) as Project;
            studyClone.title = action.payload
            console.log("setProjectTitle: ", JSON.parse(JSON.stringify(studyClone)));
            apiUpdateStudy(studyClone)
        },
        createQuery: (state, action: PayloadAction<{ title?:string, dataset: CohortDataset } | undefined>) => {
            const queryId = randomInt()
            const title = isDefined(action.payload) && isDefined(action.payload.title) ? action.payload.title : 'Default Cohort Title'

            const dataset = action.payload!.dataset
            state.study!.queries.unshift(
                {
                    id: queryId,
                    active: true,
                    title: title,
                    period: defaultQueryPeriod(dataset),
                    groups: [],
                    trackable: false,
                    pathwayStatus: 'NONE',
                    dataset: dataset
                }
            )
            state.queryId = queryId
            const groupId = _addGroup(state.study, queryId)
            updateActiveKeys(state, queryId, groupId)
            _addCriterion(state.study, {queryId, groupId})
            return state
        },
        setActiveKey: (state, action: PayloadAction<{ queryId: number, activeKey: string | string[] }>) => {
            updateActiveKeys(state, action.payload.queryId, action.payload.activeKey)
        },
        deleteQueryFromState: (state, action: PayloadAction<number>) => {
            const deletingCurrentQuery = state.queryId === action.payload
            state.study!.queries = state.study!.queries.filter(i => i.id !== action.payload)
            if (deletingCurrentQuery) {
                if (isDefined(state.study!.queries) && state.study!.queries.length > 0) {
                    state.queryId = state.study!.queries[0].id
                } else {
                    state.queryId = null
                }
                state.groupId = null
                state.criterionId = null
            }
        },
        setQueryTitle: (state, action: PayloadAction<{ queryId: number, title: string }>) => {
            const localQuery = findQuery(state.study, action.payload.queryId)!;
            localQuery.title = action.payload.title;

            // clone to prevent reloading of query as not everything is saved to server yet
            const studiesClone = JSON.parse(JSON.stringify(state.studies));
            const study = findStudy(studiesClone, state.study.id)!
            const query = findQuery(study, action.payload.queryId)
            if (query) {
                query!.title = action.payload.title
                if (query.id > 0) {
                    apiUpdateQuery(query)
                }
            }
        },
        changeQueryPeriod: (state, action: PayloadAction<{ queryId: number, props: any }>) => {
            const query = findQuery(state.study!, action.payload.queryId)!
            const newPeriod = Object.assign({}, query.period, action.payload.props)
            query.period = newPeriod
        },
        resetQueryPeriod: (state, action: PayloadAction<number>) => {
            const query = findQuery(state.study!, action.payload)!
            query.period = defaultQueryPeriod(query.dataset)
        },
        requestQuery: (state, action: PayloadAction) => {
            // TODO: remove?
            state.isFetchingQuery = true
        },
        receiveQuery: (state, action: PayloadAction<{ queryId: number, query: Query }>) => {
            state.isFetchingQuery = false
            const index = state.study!.queries.findIndex(i => i.id.toString() === action.payload.queryId.toString())
            state.study!.queries[index] = action.payload.query
        },
        mergeQueryRequests: (state, action: PayloadAction<any>) => {
            for (const [queryId, containerObj] of Object.entries(action.payload)) {
                const query = findQuery(state.study!, Number(queryId))!
                // @ts-ignore
                query.sparkQuery = containerObj.sparkQuery
                if (!_.isNil(query.sparkQuery) && !_.isNil(query.sparkQuery.response)) {
                    const response = JSON.parse(query.sparkQuery.response)
                    if (!_.isNil(response)) {
                        query.trackable = response.trackable
                    }
                }
                // this is populated in a refresh request only
                query.pathwayStatus = (containerObj as any).pathwayStatus
                query.unread = (containerObj as any).unread
            }
        },
        copyQuery: (state, action: PayloadAction<number>) => {
            const study = state.study
            const query = findQuery(study, action.payload)
            const clone: Query = JSON.parse(JSON.stringify(query))
            const newQuery = {
                id: randomInt(),
                title: generateCopyLabel(clone.title),
                period: clone.period,
                groups: clone.groups,
                pathwayStatus: 'NONE',
                dataset: clone.dataset,
            }
            if (isDefined(newQuery.groups)) {
                newQuery.groups.forEach(j => {
                    j.id = randomId()
                    if (isDefined(j.criteria)) {
                        j.criteria.forEach(k => {
                            k.id = randomId()
                        })
                    }
                })
            }
            // @ts-ignore
            _addQuery(state, newQuery)
        },
        addGroup: (state, action: PayloadAction<number>) => {
            const queryId = action.payload
            const groupId = _addGroup(state.study, queryId)
            updateActiveKeys(state, queryId, groupId)
            _addCriterion(state.study, {queryId, groupId})
        },
        deleteGroup: (state, action: PayloadAction<{ queryId: number, groupId: string }>) => {
            _deleteGroup(state.study, action.payload)
        },
        copyGroup: (state, action: PayloadAction<{ queryId: number, groupId: string }>) => {
            _copyGroup(state.study, action.payload)
        },
        groupOperatorChanged: (state, action: PayloadAction<{ queryId: number, groupId: string, value: any }>) => {
            setOperatorOfGroup(state.study, action.payload)
        },
        setGroupTitle: (state, action: PayloadAction<{ queryId: number, groupId: string, title: string }>) => {
            _setGroupTitle(state.study, action.payload)
        },
        addCriterion: (state, action: PayloadAction<CriterionPayload>) => {
            _addCriterion(state.study!, action.payload)
        },
        deleteCriterion: (state, action: PayloadAction<{ queryId: number, groupId: string, criterionId: string }>) => {
            _deleteCriterion(state.study!, action.payload)
        },
        criterionOperatorChanged: (state, action: PayloadAction<{ queryId: number, groupId: string, criterionId: string, value: any }>) => {
            _operatorCriterion(state.study!, action.payload)
        },
        setDataset: (state, action: PayloadAction<{ queryId: number, dataset: CohortDataset }>) => {
            const query: Query = findQuery(state.study!, action.payload.queryId)!
            query.dataset = action.payload.dataset
            query.period = defaultQueryPeriod(query.dataset)
        },
        resetCriteria: (state, action: PayloadAction<number>) => {
            const queryId = action.payload;
            const query: Query = findQuery(state.study!, queryId)!
            query.groups.forEach(group => _deleteGroup(state.study, { queryId: query.id, groupId: group.id }));
            const groupId = _addGroup(state.study, query.id)
            updateActiveKeys(state, query.id, groupId)
            _addCriterion(state.study, {queryId: query.id, groupId})
        }
    },
    extraReducers: builder => {
        builder.addCase(fetchProjects.pending, (state) => {
            if (!_.isNil(state.studies)) {
                state.studies = undefined
            }
        })
        builder.addCase(fetchProjects.fulfilled, (state, action) => {
            state.studies = action.payload
        })
        builder.addCase(fetchExampleProjects.pending, (state) => {
            if (!_.isNil(state.studies)) {
                state.studies = undefined
            }
        })
        builder.addCase(fetchExampleProjects.fulfilled, (state, action) => {
            state.studies = action.payload
        })
        builder.addCase(markQueryRead.fulfilled, (state, action) => {
            const updatedQuery = action.payload
            if (updatedQuery?.id !== undefined) {
                const index = state.study.queries.findIndex(query => updatedQuery.id === query.id)
                if (index > -1) {
                    state.study.queries[index] = updatedQuery
                }
            }
        })
        builder.addCase(fetchColleagues.fulfilled, (state, action) => {
            state.colleagues = action.payload
        })
        builder.addCase(fetchSharedProjects.fulfilled, (state, action) => {
            state.studies = action.payload
        })
        builder.addCase(fetchSharedProjectsUnreadCount.fulfilled, (state, action) => {
            state.sharedProjectsUnreadCount = action.payload.unreadCount
        })
    }
})

const generateCopyLabel = (cohortTitle: string): string => {

    const maxLabelLength = 100;
    const copyPrefixRegexMatch = /^Copy [0-9]+/.exec(cohortTitle)
    if (copyPrefixRegexMatch === null) {
        const prefix = 'Copy 1 ';
        return prefix + cohortTitle.substring(0, (maxLabelLength - prefix.length))
    }

    const existingCopyPrefix = copyPrefixRegexMatch[0]
    const existingCopyNumber = Number(existingCopyPrefix.replace(/\D/g, "").trim())
    const newCopyNumber = existingCopyNumber + 1
    const newCopyText = 'Copy ' + newCopyNumber + ' '
    return newCopyText + cohortTitle.substring(newCopyText.length, maxLabelLength)
}

const _addQuery = (state: CurrentState, query: Query) => {
    state.study!.queries.unshift(query)
    state.queryId = query.id
    initActiveKeys(state, state.queryId)
}

const defaultQueryPeriod = (dataset: CohortDataset) => {
    const { min, max } = monthsRange(dataset)
    return {
        from: min,
        to: max,
        lookback: null,
        followup: null
    }
}

export const initActiveKeys = (state: CurrentState, queryId: number | null) => {
    if (!_.isNil(queryId)) {
        const query: Query = findQuery(state.study!, queryId)!
        // set keys if one isn't already set
        if (_.isNil(state.activeKeys[queryId])) {
            state.activeKeys[queryId] = query.groups.map(i => i.id)
        }
    }
}

export const updateActiveKeys = (state: CurrentState, queryId: number, keys: string | string[]) => {
    if (Array.isArray(keys)) {
        state.activeKeys[queryId] = keys
    } else {
        if (Array.isArray(state.activeKeys[queryId])) {
            state.activeKeys[queryId].push(keys)
        } else {
            state.activeKeys[queryId] = [keys]
        }
    }
}

export const {
    saveStudy, setCurrentQuery, setCurrentStudy, setCurrentCriterion, setProjectTitle, createQuery,
    setActiveKey, deleteQueryFromState, setQueryTitle, changeQueryPeriod, resetQueryPeriod,
    requestQuery, receiveQuery, mergeQueryRequests, copyQuery, addGroup, deleteGroup, copyGroup,
    groupOperatorChanged, setGroupTitle, addCriterion, deleteCriterion, criterionOperatorChanged, setDataset, resetCriteria
} = cohortBuilderSlice.actions;
export const selectProjects = (state: RootState) => state.current.studies
export const selectSharedProjectsUnreadCount = (state: RootState) => state.current.sharedProjectsUnreadCount
export const selectCurrent = (state: RootState) => getCurrent(state)
export const selectActiveKeys = (state: RootState) => state.current.activeKeys
export const selectProject = (projectId: number | undefined) => (state: RootState) => {
    if (projectId === undefined) {
        return state.current.study
    }
    return state?.current?.studies?.find(project => project.id === projectId)
}
export const selectColleagues = (state: RootState) => state.current.colleagues

export default cohortBuilderSlice.reducer